<template>
  <form @submit.prevent="signIn" class="signin-form">
    <div class="signin-form__fields">
      <!-- Email Field -->
      <div class="form-field">
        <label class="form-field__label">Email address</label>
        <div class="form-field__input-wrapper" :class="{ 'form-field__input-wrapper--error': emailError.$message }">
          <svg class="form-field__icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M22 6L12 13L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <input
            type="email"
            class="form-field__input"
            v-model="emailAddress"
            @blur="validateMethod('email')"
            placeholder="Enter your email"
            :disabled="loading"
            autocomplete="email"
          />
        </div>
        <p v-if="emailError.$message" class="form-field__error">{{ emailError.$message }}</p>
      </div>

      <!-- Password Field -->
      <div class="form-field">
        <label class="form-field__label">Password</label>
        <div class="form-field__input-wrapper" :class="{ 'form-field__input-wrapper--error': passwordError.$message }">
          <svg class="form-field__icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"/>
            <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <input
            :type="inputType"
            class="form-field__input"
            v-model="pass"
            @blur="validateMethod('password')"
            placeholder="Enter your password"
            :disabled="loading"
            autocomplete="current-password"
          />
          <button type="button" class="form-field__toggle" @click="togglePassword" :disabled="!payload.password">
            <svg v-if="inputType === 'password'" width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
            </svg>
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65661 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2047 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.5719 9.14351 13.1984C8.99262 12.8248 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2218 9.18488 10.8538C9.34884 10.4859 9.58525 10.1546 9.88 9.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M1 1L23 23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <p v-if="passwordError.$message" class="form-field__error">{{ passwordError.$message }}</p>
      </div>
    </div>

    <!-- Forgot Password Link -->
    <div class="signin-form__forgot">
      <router-link to="/onboarding/request-link" class="forgot-link">
        Forgot password?
      </router-link>
    </div>

    <!-- Submit Button -->
    <button type="submit" class="signin-form__submit" :disabled="loading">
      <span v-if="!loading">Sign in</span>
      <ButtonLoader v-else color="white" size="medium" />
    </button>
  </form>
</template>

<script>
import { mapState, mapActions } from "vuex";
import useValidate from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import ButtonLoader from "@/components/notification/ButtonLoader.vue";

export default {
  name: "SigninForm",

  components: {
    ButtonLoader,
  },

  emits: ["signIn"],

  data() {
    return {
      v$: useValidate(),
      payload: { email: "", password: "" },
      passwordError: {},
      emailError: {},
      inputType: "password",
    };
  },

  validations() {
    return {
      payload: {
        email: { required, email },
        password: { required },
      },
    };
  },

  computed: {
    ...mapState({
      loading: (state) => state.loadingModule.signinLoading,
      success: (state) => state.loadingModule.signinSuccess,
    }),

    emailAddress: {
      get() {
        return this.payload.email;
      },
      set(value) {
        this.payload.email = value;
      },
    },

    pass: {
      get() {
        return this.payload.password;
      },
      set(value) {
        this.payload.password = value;
      },
    },
  },

  methods: {
    ...mapActions("loadingModule", ["signinSuccess"]),

    signIn() {
      this.v$.$validate();
      const hasValue = Object.keys(this.payload).every(
        (key) => this.payload[key]
      );
      if (this.v$.$errors.length === 0 && hasValue) {
        this.$emit("signIn", this.payload);
      }
    },

    togglePassword() {
      this.inputType = this.inputType === "password" ? "text" : "password";
    },

    validateMethod(field) {
      switch (field) {
        case "email":
          this.v$.$reset();
          this.v$.payload.email.$touch();
          if (this.v$.$errors.length === 0) return (this.emailError = {});
          this.emailError = this.v$.$errors[0];
          break;

        case "password":
          this.v$.$reset();
          this.v$.payload.password.$touch();
          if (this.v$.$errors.length === 0) return (this.passwordError = {});
          this.passwordError = this.v$.$errors[0];
          break;

        default:
          break;
      }
    },
  },

  watch: {
    success(newValue) {
      deep: true;
      immediate: true;
      if (newValue === true) {
        this.payload = { email: "", password: "" };
        this.signinSuccess(false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.signin-form {
  width: 100%;

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 16px;
  }

  &__forgot {
    text-align: right;
    margin-bottom: 24px;
  }

  &__submit {
    width: 100%;
    background: #4D84FF;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: white;
    padding: 14px 24px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 50px;
    margin-bottom: 24px;

    &:hover:not(:disabled) {
      background: #3B6FE8;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(77, 132, 255, 0.3);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
}

.form-field {
  &__label {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    transition: all 0.2s ease;

    &:focus-within {
      border-color: #4D84FF;
      background: white;
      box-shadow: 0 0 0 3px rgba(77, 132, 255, 0.1);
    }

    &--error {
      border-color: #EF4444;
      background: #FEF2F2;

      &:focus-within {
        border-color: #EF4444;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
      }

      .form-field__icon {
        color: #EF4444;
      }
    }
  }

  &__icon {
    position: absolute;
    left: 14px;
    color: #94A3B8;
    pointer-events: none;
    flex-shrink: 0;
  }

  &__input {
    width: 100%;
    padding: 14px 14px 14px 44px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    background: transparent;
    border: none;
    outline: none;
    border-radius: 10px;

    &::placeholder {
      color: #94A3B8;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__toggle {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #94A3B8;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease;

    &:hover:not(:disabled) {
      color: #64748B;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__error {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #EF4444;
    margin: 6px 0 0 0;
  }
}

.forgot-link {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #64748B;
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: #4D84FF;
  }
}
</style>
